<template>
  <v-form ref="form" v-model="valid" lazy-validation class="pa-2">
    <v-row dense>
      <v-col sm="12" md="8">
        <v-text-field
          v-model="formData.label"
          :rules="nameRules"
          :label="$t('labels.name')"
          variant="outlined"
          density="compact"
          required
        ></v-text-field>
      </v-col>
      <v-col sm="12" md="4" class="d-flex align-center">
        <v-select
          v-model="formData.inputType"
          :items="types"
          item-title="title"
          item-value="value"
          :label="$t('labels.type')"
          variant="outlined"
          density="compact"
          required
        ></v-select>
      </v-col>
      <v-col sm="12" md="8" v-if="formData.contentType != 'date'">
        <v-text-field
          v-model="formData.defaultValue"
          :rules="[validateNoAccents]"
          :label="$t('labels.default-value')"
          variant="outlined"
          density="compact"
          required
          :error-messages="formData.errorMessage"
        ></v-text-field>
      </v-col>
      <v-col sm="12" md="8" v-else>
        <v-menu
          v-model="menu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ props }">
            <v-text-field
              v-model="date"
              :label="$t('labels.default-value')"
              variant="outlined"
              density="compact"
              required
              prepend-inner-icon="mdi-calendar-blank"
              readonly
              clearable
              v-bind="props"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="date"
            @input="menu = false"
            color="primary"
            :hide-header="true"
            @update:modelValue="setDateAsDefault"
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col sm="12" md="4" class="d-flex align-center">
        <v-select
          v-model="formData.contentType"
          :items="contents"
          item-title="title"
          item-value="value"
          :label="$t('labels.content')"
          variant="outlined"
          density="compact"
          required
        ></v-select>
      </v-col>

      <v-col cols="auto" class="d-flex align-center">
        <v-checkbox
          v-model="formData.isConstant"
          :label="$t('strings.use-like-constant')"
          color="secondary"
          :hide-details="true"
        ></v-checkbox>
        <v-tooltip location="top" max-width="320">
          <template v-slot:activator="{ props }">
            <v-icon
              v-bind="props"
              icon="mdi-information-outline"
              size="small"
              class="ml-1"
              color="info"
            ></v-icon>
          </template>
          <div>
            <div class="font-weight-bold mb-1">
              {{ $t("strings.use-like-constant-tooltip-title") }}
            </div>
            <div class="mb-2">
              {{ $t("strings.use-like-constant-tooltip-enabled") }}
            </div>
            <div class="mb-2">
              {{ $t("strings.use-like-constant-tooltip-disabled") }}
            </div>
            <div>{{ $t("strings.use-like-constant-tooltip-fallback") }}</div>
          </div>
        </v-tooltip>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { inject, ref, toRefs, reactive, watch, computed } from "vue";
import { useI18n } from "vue-i18n";

export default {
  name: "OperatorInput",
  props: {
    target: Object,
  },
  emits: ["updateFormData"],
  setup(props, { emit }) {
    const valid = inject("valid");
    const menu = ref(false);
    const date = ref(
      props.target.userData?.contentType === "date"
        ? new Date(props.target.userData.defaultValue)
        : null,
    );

    const dateFormatted = computed(() => {
      if (date.value && formData.contentType === "date") {
        const d = new Date(date.value);
        const iso = new Date(
          d.getTime() - d.getTimezoneOffset() * 60000,
        ).toISOString();
        return d instanceof Date && !isNaN(d) ? iso : "";
      }
      return "";
    });
    const setDateAsDefault = () => {
      formData.defaultValue = dateFormatted.value;
    };

    const { t } = useI18n();
    const types = ref([
      { title: "Query", value: "query" },
      { title: "Header", value: "header" },
      { title: "Body", value: "body" },
    ]);
    const contents = ref([
      { title: "Text", value: "text" },
      { title: "Date (current)", value: "date" },
      { title: "Json", value: "json" },
    ]);
    const nameRules = ref([(v) => !!v || t("strings.name-is-required")]);

    const formData = reactive(
      JSON.parse(JSON.stringify(props.target.userData)) || {
        label: props.target.id,
        inputType: "query",
        contentType: "text",
        defaultValue: "",
        isConstant: false,
        errorMessage: "",
      },
    );

    // Validation function to remove accented characters
    const validateNoAccents = (value) => {
      const regex = /[àèìòùÀÈÌÒÙáéíóúÁÉÍÓÚâêîôûÂÊÎÔÛäëïöüÄËÏÖÜãñõÃÑÕçÇ]/g;
      if (regex.test(value)) {
        formData.errorMessage = t("strings.invalid-characters");
        return false;
      }
      formData.errorMessage = "";
      return true;
    };

    // Watch to monitor defaultValue only if contentType is json
    watch(
      () => formData.defaultValue,
      (newValue) => {
        if (formData.contentType === "json") {
          validateNoAccents(newValue);
        }
      },
    );

    // Watch to monitor contentType and validate defaultValue if necessary
    watch(
      () => formData.contentType,
      (newContentType) => {
        if (newContentType === "json") {
          validateNoAccents(formData.defaultValue);
        }
      },
    );

    watch(
      formData,
      (newFormData) => {
        emit("updateFormData", newFormData);
      },
      { deep: true, immediate: true },
    );

    return {
      valid,
      types,
      contents,
      nameRules,
      formData,
      ...toRefs(formData),
      menu,
      date,
      dateFormatted,
      setDateAsDefault,
      validateNoAccents,
    };
  },
};
</script>
